import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import {
    ArrowLeft, Loader2, User, Phone, Mail, Euro, Pencil, Save, X, MapPin, Truck, Package,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import CalculMensuelSection from '../components/CalculMensuelSection';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
    if (!iso) return '-';
    try { return format(new Date(iso + 'T00:00:00'), 'dd/MM/yyyy', { locale: fr }); }
    catch { return iso; }
};

const firstOfMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Retourne { from, to } pour le mois en cours.
const rangeCurrentMonth = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const end = String(new Date(y, d.getMonth() + 1, 0).getDate()).padStart(2, '0');
    return { from: `${y}-${m}-01`, to: `${y}-${m}-${end}` };
};

// Solde = Σ(nb_jour) × tarif/jour + Σ(panier) × 25 + Σ(autres)
const PANIER_MONTANT = 25;
const computeSolde = (total, tarifJour) => (
    (total?.nb_jour || 0) * (tarifJour || 0) + (total?.panier || 0) * PANIER_MONTANT + (total?.autres || 0)
);

const MONTH_LABEL = (yyyyMmDd) => {
    try {
        const d = new Date(yyyyMmDd + 'T00:00:00');
        return format(d, 'MMMM yyyy', { locale: fr });
    } catch { return yyyyMmDd; }
};

// Idem mais a partir d'une cle YYYY-MM (format des calculs mensuels).
const MONTH_LABEL_KEY = (yyyyMm) => {
    try {
        const d = new Date(yyyyMm + '-01T00:00:00');
        return format(d, 'MMMM yyyy', { locale: fr });
    } catch { return yyyyMm; }
};

export default function DemenageurDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [fromDate, setFromDate] = useState(firstOfMonth());
    const [toDate, setToDate] = useState(todayISO());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Soldes mois en cours & calculs mensuels (pour le solde total + 3 derniers mois)
    const [soldeCurrent, setSoldeCurrent] = useState(null);
    const [allCalculs, setAllCalculs] = useState([]);

    // Edition des infos
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({ nom: '', telephone: '', email: '', tarif_jour: '' });
    const [saving, setSaving] = useState(false);

    const fetchDetail = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/api/exploitation/demenageurs/${id}?from_date=${fromDate}&to_date=${toDate}`,
                { withCredentials: true },
            );
            setData(data);
        } catch {
            setData(null);
            toast.error('Impossible de charger la fiche');
        } finally { setLoading(false); }
    }, [id, fromDate, toDate]);

    const fetchSoldes = useCallback(async () => {
        const cur = rangeCurrentMonth();
        try {
            const [r1, rCalc] = await Promise.all([
                axios.get(`${API}/api/exploitation/demenageurs/${id}?from_date=${cur.from}&to_date=${cur.to}`, { withCredentials: true }),
                axios.get(`${API}/api/exploitation/demenageurs/${id}/calculs`, { withCredentials: true }),
            ]);
            setSoldeCurrent({ ...cur, total: r1.data.total, tarif: r1.data.demenageur.tarif_jour });
            setAllCalculs(rCalc.data || []);
        } catch {
            setSoldeCurrent(null);
            setAllCalculs([]);
        }
    }, [id]);

    useEffect(() => { fetchDetail(); }, [fetchDetail]);
    useEffect(() => { fetchSoldes(); }, [fetchSoldes]);

    const startEdit = () => {
        if (!data) return;
        setForm({
            nom: data.demenageur.nom,
            telephone: data.demenageur.telephone,
            email: data.demenageur.email,
            tarif_jour: String(data.demenageur.tarif_jour),
        });
        setEditing(true);
    };

    const saveEdit = async () => {
        if (!form.nom.trim()) return toast.error('Nom obligatoire');
        if (!form.telephone.trim()) return toast.error('Téléphone obligatoire');
        if (form.tarif_jour === '' || Number(form.tarif_jour) < 0) return toast.error('Tarif/jour invalide');
        setSaving(true);
        try {
            await axios.put(`${API}/api/exploitation/demenageurs/${id}`, {
                nom: form.nom.trim(),
                telephone: form.telephone.trim(),
                email: form.email.trim(),
                tarif_jour: Number(form.tarif_jour),
            }, { withCredentials: true });
            toast.success('Fiche mise à jour');
            setEditing(false);
            fetchDetail();
            fetchSoldes();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally { setSaving(false); }
    };

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="demenageur-detail-page">
            <div className="flex items-center gap-3 mb-6">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/exploitation/pointage-demenageurs?tab=demenageurs')}
                    className="text-slate-700 border-slate-300 hover:bg-slate-100"
                    data-testid="back-btn"
                >
                    <ArrowLeft size={16} className="mr-1" /> Retour
                </Button>
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Fiche déménageur
                    </h1>
                    <p className="mt-1 text-slate-500 text-sm">
                        Informations et historique des prestations.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : !data ? (
                <p className="text-sm text-slate-500 py-10 text-center">Fiche introuvable.</p>
            ) : (
                <div className="space-y-6">
                    {/* Section infos (éditable) */}
                    <Card className="bg-white rounded-xl border border-slate-200 shadow-sm">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    Informations
                                </h2>
                                {!editing ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={startEdit}
                                        className="text-slate-700 border-slate-300 hover:bg-slate-100"
                                        data-testid="edit-btn"
                                    >
                                        <Pencil size={14} className="mr-1" /> Modifier
                                    </Button>
                                ) : (
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setEditing(false)}
                                            disabled={saving}
                                        >
                                            <X size={14} className="mr-1" /> Annuler
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={saveEdit}
                                            disabled={saving}
                                            className="bg-[#2e7cd6] hover:bg-[#2568b5] text-white"
                                            data-testid="save-btn"
                                        >
                                            {saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} className="mr-1" /> Enregistrer</>}
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {!editing ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
                                            <User size={18} className="text-purple-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs text-slate-500 uppercase tracking-wide">Nom</p>
                                            <p className="text-sm font-semibold text-slate-900 truncate">{data.demenageur.nom}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                                            <Phone size={18} className="text-emerald-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs text-slate-500 uppercase tracking-wide">Téléphone</p>
                                            <p className="text-sm font-semibold text-slate-900 truncate">{data.demenageur.telephone || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                                            <Mail size={18} className="text-amber-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs text-slate-500 uppercase tracking-wide">Email</p>
                                            <p className="text-sm font-semibold text-slate-900 truncate">{data.demenageur.email || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
                                            <Euro size={18} className="text-purple-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs text-slate-500 uppercase tracking-wide">Tarif / jour</p>
                                            <p className="text-sm font-semibold text-slate-900">{data.demenageur.tarif_jour.toFixed(2)} €</p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div>
                                        <Label className="text-xs text-slate-600">Nom *</Label>
                                        <Input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} data-testid="edit-nom" />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-slate-600">Téléphone *</Label>
                                        <Input value={form.telephone} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))} data-testid="edit-tel" />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-slate-600">Email</Label>
                                        <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="edit-email" />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-slate-600">Tarif / jour (€) *</Label>
                                        <Input type="number" step="0.01" min="0" value={form.tarif_jour} onChange={e => setForm(f => ({ ...f, tarif_jour: e.target.value }))} data-testid="edit-tarif" />
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Section Soldes mois précédent + mois en cours */}
                    <Card className="bg-white rounded-xl border border-slate-200 shadow-sm">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    Soldes
                                </h2>
                                <span className="text-xs text-slate-500 italic">
                                    Jours × tarif/jour + paniers × 25&nbsp;€ + autres
                                </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {/* Solde total (somme des soldes finaux de tous les mois) */}
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="solde-total">
                                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                                        Solde total
                                        {allCalculs.length > 0 && (
                                            <span className="normal-case font-normal text-slate-400"> — {allCalculs.length} mois</span>
                                        )}
                                    </p>
                                    {allCalculs.length > 0 ? (
                                        <>
                                            <p className="text-2xl font-bold text-slate-900" data-testid="solde-total-value">
                                                {allCalculs.reduce((s, c) => s + (Number(c.solde_final) || 0), 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                            </p>
                                            <div className="mt-3 pt-3 border-t border-slate-200 space-y-1.5" data-testid="solde-recents">
                                                {/* Affiche les 3 derniers mois (calculs deja tries par month desc cote backend) */}
                                                {allCalculs.slice(0, 3).map(c => (
                                                    <div key={c.id || c.month} className="flex items-center justify-between text-xs">
                                                        <span className="text-slate-500 capitalize">Solde {MONTH_LABEL_KEY(c.month)}</span>
                                                        <span className={`font-semibold ${Number(c.solde_final) >= 0 ? 'text-slate-900' : 'text-red-600'}`}>
                                                            {Number(c.solde_final).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <p className="text-sm text-slate-400 italic">Aucun calcul mensuel cree</p>
                                    )}
                                </div>
                                {/* Mois en cours */}
                                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4" data-testid="solde-current">
                                    <p className="text-xs text-[#2e7cd6] uppercase tracking-wide mb-1 font-semibold">
                                        Mois en cours {soldeCurrent && <span className="normal-case font-normal text-blue-500">— {MONTH_LABEL(soldeCurrent.from)}</span>}
                                    </p>
                                    {soldeCurrent ? (
                                        <>
                                            <p className="text-2xl font-bold text-[#2e7cd6]">
                                                {computeSolde(soldeCurrent.total, soldeCurrent.tarif).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                            </p>
                                            <div className="text-xs text-slate-500 mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
                                                <span>{soldeCurrent.total.nb_jour}j × {soldeCurrent.tarif.toFixed(2)}€</span>
                                                <span>+ {soldeCurrent.total.panier} panier × 25€</span>
                                                <span>+ {soldeCurrent.total.autres.toFixed(2)}€ autres</span>
                                            </div>
                                        </>
                                    ) : (
                                        <p className="text-sm text-slate-400">—</p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Section Calculs mensuels */}
                    <CalculMensuelSection demenageurId={id} />

                    {/* Filtres période */}
                    <Card className="bg-white rounded-xl border border-slate-200 shadow-sm">
                        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
                            <div>
                                <Label className="text-xs text-slate-600">Du</Label>
                                <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-40" data-testid="from" />
                            </div>
                            <div>
                                <Label className="text-xs text-slate-600">Au</Label>
                                <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-40" data-testid="to" />
                            </div>
                            <div className="text-xs text-slate-500 ml-auto self-center">
                                {data.prestations.length} prestation{data.prestations.length > 1 ? 's' : ''} sur la période
                            </div>
                        </CardContent>
                    </Card>

                    {/* Liste prestations */}
                    <Card className="bg-white rounded-xl border border-slate-200 shadow-sm">
                        <CardContent className="p-0">
                            {data.prestations.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center py-10">Aucune prestation sur la période.</p>
                            ) : (
                                <>
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-slate-50/70">
                                                <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Client</TableHead>
                                                <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Date</TableHead>
                                                <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Type</TableHead>
                                                <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Ville</TableHead>
                                                <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold text-right whitespace-nowrap">Nb jour</TableHead>
                                                <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold text-right whitespace-nowrap">Autres</TableHead>
                                                <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Panier</TableHead>
                                                <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Retard</TableHead>
                                                <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Remarque</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {data.prestations.map((p, idx) => {
                                                const type = (p.type_prestation || '').toLowerCase();
                                                const isDechargement = type.includes('déchargement') || type.includes('dechargement') || type === 'livraison';
                                                const ville = isDechargement ? p.ville_arrivee : p.ville_depart;
                                                return (
                                                    <TableRow key={`${p.entry_id}-${idx}`} className="hover:bg-slate-50" data-testid={`pr-row-${idx}`}>
                                                        <TableCell className="px-5 text-sm text-slate-900 font-medium whitespace-nowrap">
                                                            <span className="inline-flex items-center gap-2">
                                                                {p.event_type === 'livraison'
                                                                    ? <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-50 text-amber-500" title="Livraison"><Package size={14} /></span>
                                                                    : <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 text-[#2e7cd6]" title="Déménagement"><Truck size={14} /></span>}
                                                                {p.client_nom || '-'}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell className="px-5 text-xs text-slate-600 whitespace-nowrap">{formatDate(p.date)}</TableCell>
                                                        <TableCell className="px-5 whitespace-nowrap">
                                                            {p.type_prestation
                                                                ? <Badge variant="outline" className="text-xs bg-slate-50 text-slate-700 border-slate-200">{p.type_prestation}</Badge>
                                                                : <span className="text-slate-400 text-xs">-</span>}
                                                        </TableCell>
                                                        <TableCell className="px-5 whitespace-nowrap text-slate-600 text-xs">
                                                            {ville
                                                                ? <span className="inline-flex items-center gap-1"><MapPin size={12} className="text-slate-400" />{ville}</span>
                                                                : <span className="text-slate-400">-</span>}
                                                        </TableCell>
                                                        <TableCell className="px-5 text-right text-sm">{p.nb_jour}</TableCell>
                                                        <TableCell className="px-5 text-right text-sm">{p.autres}</TableCell>
                                                        <TableCell className="px-5">
                                                            {p.panier
                                                                ? <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">Oui</Badge>
                                                                : <Badge variant="outline" className="text-xs bg-slate-50 text-slate-500 border-slate-200">Non</Badge>}
                                                        </TableCell>
                                                        <TableCell className="px-5">
                                                            {p.retard
                                                                ? <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">Oui</Badge>
                                                                : <Badge variant="outline" className="text-xs bg-slate-50 text-slate-500 border-slate-200">Non</Badge>}
                                                        </TableCell>
                                                        <TableCell className="px-5 text-xs text-slate-600 max-w-[240px] truncate" title={p.remarque}>{p.remarque || '-'}</TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                    {/* Footer totaux */}
                                    <div className="flex items-center justify-end gap-6 bg-blue-50 border-t border-blue-200 px-5 py-3 text-sm" data-testid="totals">
                                        <span className="text-slate-600 font-semibold">Totaux :</span>
                                        <span><span className="text-slate-500">Jour</span> <strong className="text-slate-900">{data.total.nb_jour}</strong></span>
                                        <span><span className="text-slate-500">Autres</span> <strong className="text-slate-900">{data.total.autres}</strong></span>
                                        <span><span className="text-slate-500">Panier</span> <strong className="text-slate-900">{data.total.panier}</strong></span>
                                        <span><span className="text-slate-500">Retard</span> <strong className="text-slate-900">{data.total.retard}</strong></span>
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
